// src/hooks/useProfile.js
// A lightweight hook to fetch and cache the unified profile in-memory.
// Safe by default: no persistence to localStorage. Cache auto-clears on sign-out.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../supabaseClient';
import {
  getMyProfile,
  updateMyBasics as rpcUpdateBasics,
  getMyNutritionPreferences,
  upsertMyNutritionPreferences,
} from '../api/profile';

let profileCache = null; // { profile, nutrition }
let profileCacheAt = 0;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

export function clearProfileCache() {
  profileCache = null;
  profileCacheAt = 0;
}

async function fetchCombined() {
  const [profRes, nutRes] = await Promise.all([
    getMyProfile(),
    getMyNutritionPreferences(),
  ]);

  const { data: profRows, error: pErr } = profRes || {};
  if (pErr) throw pErr;

  // Be tolerant of nutrition errors (missing table/row). Default to empty prefs.
  let nut = { proteins: [], carbs: [], fats: [] };
  if (nutRes && !nutRes.error && nutRes.data) {
    nut = nutRes.data;
  }

  const profile = Array.isArray(profRows) ? profRows[0] : profRows?.[0] || null;
  return { profile, nutrition: nut };
}

export default function useProfile(options = {}) {
  const { refreshOnMount = false, ttlMs = CACHE_TTL_MS } = options;
  const [data, setData] = useState(profileCache);
  const [loading, setLoading] = useState(!profileCache);
  const [error, setError] = useState(null);
  const mounted = useRef(true);

  const isExpired = useMemo(() => Date.now() - profileCacheAt > ttlMs, [ttlMs]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const combined = await fetchCombined();
      if (!mounted.current) return;
      profileCache = combined;
      profileCacheAt = Date.now();
      setData(combined);
    } catch (e) {
      if (!mounted.current) return;
      setError(e);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    const needsFetch = refreshOnMount || !profileCache || isExpired;
    if (needsFetch) load(); else setLoading(false);

    const { data: sub } = supabase.auth.onAuthStateChange((_evt, session) => {
      if (!session) {
        clearProfileCache();
        setData(null);
        setLoading(false);
        setError(null);
        return;
      }
      const cacheMissing = !profileCache || !profileCache.profile;
      if (cacheMissing || isExpired) {
        load();
      }
    });
    return () => {
      mounted.current = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, [isExpired, load, refreshOnMount]);

  const refresh = useCallback(async () => {
    clearProfileCache();
    await load();
  }, [load]);

  const updateBasics = useCallback(async (payload) => {
    const res = await rpcUpdateBasics(payload);
    // refresh cache on success
    if (!res.error) await refresh();
    return res;
  }, [refresh]);

  const updateNutrition = useCallback(async (prefs) => {
    const res = await upsertMyNutritionPreferences(prefs);
    if (!res.error) await refresh();
    return res;
  }, [refresh]);

  return {
    data, // { profile, nutrition }
    loading,
    error,
    refresh,
    updateBasics,
    updateNutrition,
  };
}
